import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import {
	AccountInfo,
	AuthenticationResult,
	BrowserAuthError,
	IPublicClientApplication,
	InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { Method } from "axios";
import httpService from "../services/httpService";
import { api_s2 } from "../config";

export type ApiMethod = Method;

/**
 * Standalone API caller — resolves MSAL account at call-time via
 * instance.getAllAccounts() so it works inside React Query queryFn
 * callbacks (no stale-closure issues with useAccount).
 */
export const callPymbleApi = async (
	instance: IPublicClientApplication,
	method: Method,
	url: string,
	options: Record<string, unknown> | null = null
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
	// MSAL v4 requires initialize() before any API call.
	// Idempotent — safe to call even if MsalProvider already initialized.
	await instance.initialize();

	let account: AccountInfo | null = null;

	try {
		const accounts = instance.getAllAccounts();
		account = accounts[0] || null;
	} catch (error) {
		console.error("callPymbleApi - Error getAllAccounts", error);
		return Promise.reject(error);
	}

	if (!account) {
		// No account yet — expected before authentication completes.
		// Return null instead of rejecting to avoid React Query retry spam.
		return null;
	}

	let response: AuthenticationResult | void;
	try {
		response = await instance.acquireTokenSilent({
			scopes: api_s2.scopes,
			account,
		});
	} catch (error) {
		// A timed-out hidden-iframe renewal can't recover on its own either,
		// so fall back to a full redirect rather than leaving the page empty.
		if (
			error instanceof InteractionRequiredAuthError ||
			(error instanceof BrowserAuthError &&
				error.errorCode === "monitor_window_timeout")
		) {
			await instance.acquireTokenRedirect({
				scopes: api_s2.scopes,
				account,
			});
			return null;
		}
		console.error("acquireTokenSilent failed:", (error as Error).message);
		return null;
	}

	if (!response?.accessToken) {
		return Promise.reject("No access token found");
	}

	const res = await httpService.request({
		method,
		url,
		...options,
		headers: {
			Authorization: `Bearer ${response.accessToken}`,
			...(options?.headers as Record<string, string> | undefined),
		},
	});

	return res?.data ?? null;
};

/**
 * Hook wrapper — returns the MSAL instance and a bound callApi for
 * convenience in hooks that still need the hook-based pattern.
 */
export function usePymbleApi() {
	const { instance } = useMsal();
	const isAuthenticated = useIsAuthenticated();

	const callApi = async (
		method: Method,
		url: string,
		options: Record<string, unknown> | null = null
	) => {
		return callPymbleApi(instance, method, url, options);
	};

	return {
		instance,
		callApi,
		isAuthenticated,
	};
}
